<template>
  <v-dialog
    v-model="canShowDialog"
    max-width="70%"
    :transition="false"
    class="aggregator-completing-data-dialog"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    @keydown.enter="onSearchByOibOrName"
  >
    <v-card
      class="v-card"
      style="max-height: 70vh; display: flex; flex-direction: column; max-width: 100%;"
    >
      <!--    Loader-->
      <v-overlay
        :value="showLoader"
        class="d-flex align-center justify-center"
        opacity="0.2"
        persistent
      >
        <v-progress-circular
          indeterminate
          size="60"
          color="primary"
        ></v-progress-circular>
      </v-overlay>

<!--      Card title-->
      <v-card-title
        class="v-card-title"
      >
        {{ dialogTitle }}
        <v-spacer/>

        <div
          class="x-image"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog"
          />
        </div>
      </v-card-title>

<!--      Card content-->
      <v-card-text
        class="v-card-text pt-2"
        style="overflow-y: auto;"
      >
        <v-row>
<!--          Filtered data-->
          <v-col
            class="border-right"
            style="max-height: 50%;"
          >
            <div
              class="subtitle"
            >
              {{ $t('road-maintenance/aggregator.find_data') }}
            </div>

<!--            OIB or name input field with search button-->
            <div
              class="oib-wrapper-col"
            >
              <text-field
                v-model="oibOrName"
                :field="oibOrNameField"
                :validation-errors="validationErrors"
                autofocus
                style="margin-bottom: 3%;"
                @clear-validation-errors="clearValidationError(oibOrNameField.key)"
              />

              <universal-button
                type="base"
                color="#65AD45"
                :label="$t('road-maintenance.search')"
                style="color: white; align-self: end;"
                @click="onSearchByOibOrName"
              />
            </div>

<!--            Filtered results title-->
            <div
              v-if="filteredResults.length"
              class="found-results-title"
            >
              {{ $t('road-maintenance/aggregator.found_results') }}:
            </div>

            <!--            Filtered results-->
            <div
              v-if="filteredResults.length"
              class="filtered-results-wrapper"
            >
              <v-row
                v-for="(filteredResult, filteredResultIndex) in filteredResults"
                :key="filteredResultIndex"
                class="result-row"
              >
                <v-col
                  class="result-col"
                  :class="{ 'result-col--active': filteredResult.id === currentResultId }"
                  @click="onSelectedResult(filteredResult)"
                >
                  <v-row
                    style="margin: 0;"
                  >
                    <v-col
                      class="result-text-col"
                    >
                      {{ filteredResult.name || '-' }}
                    </v-col>

                    <v-col
                      style="padding: 0; align-self: center;"
                      class="d-flex justify-end"
                    >
                      <v-icon>
                        mdi-arrow-right-bold
                      </v-icon>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </div>
            <div
              v-else-if="!showLoader"
              class="no-data-text"
            >
              {{ $t('base.no_data') }}
            </div>
          </v-col>

<!--          New data-->
          <v-col
            style="border-right: 1px solid #E5E7EB;"
          >
            <div
              class="subtitle"
            >
              {{ $t('road-maintenance/aggregator.new_data') }}
            </div>

            <v-row
              v-for="(field, fieldIndex) in formFields"
              :key="fieldIndex"
            >
              <v-col>
                <div
                  v-if="!field.type"
                >
                  <text-field
                    v-model="formData[field.key]"
                    :field="field"
                    :validation-errors="validationErrors"
                    @clear-validation-errors="clearValidationError(field.key)"
                  />
                </div>
                <div
                  v-else-if="field.type === 'autocomplete'"
                >
                  <autocomplete-field
                    v-model="formData[field.key]"
                    :field="field"
                    :options="field.options || {}"
                    :validation-errors="validationErrors"
                    @clear-validation-errors="clearValidationError(field.key)"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>

<!--          Current data-->
          <v-col>
            <div
              class="subtitle"
            >
              {{ $t('road-maintenance/aggregator.data_from_request') }}
            </div>

            <v-row
              v-for="(field, fieldIndex) in formFields"
              :key="fieldIndex"
            >
              <v-col>
                <div
                  v-if="!field.type"
                >
                  <text-field
                    v-model="formData[field.key + '-current']"
                    :field="field"
                    disabled
                  />
                </div>
                <div
                  v-else-if="field.type === 'autocomplete'"
                >
                  <autocomplete-field
                    v-model="formData[field.key + '-current']"
                    :field="field"
                    :options="field.options || {}"
                    disabled
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row
          style="margin: 0;"
        >
          <v-col/>
          <v-col
            class="d-flex justify-end"
          >
<!--            Cancel btn-->
            <universal-button
              type="cancel"
              style="align-self: end; font-size: 12px; margin-right: 4%;"
              @click="closeDialog"
            />

<!--            Update existing data btn,  enabled only if user select one of the filtered data-->
            <universal-button
              v-if="showUpdateBtn"
              type="save"
              :label="$t('base.update')"
              style="align-self: end; font-size: 12px; margin-right: 4%;"
              @click="saveData(true)"
            />

<!--            Create new btn-->
<!--            User can create new data from the existing one (user must select one of the filtered data)-->
<!--            or completely create a new one-->
            <universal-button
              type="save"
              :label="$t('road-maintenance/aggregator.create_new')"
              style="align-self: end; font-size: 12px;"
              @click="saveData"
            />
          </v-col>
          <v-col
            class="d-flex justify-end"
          >
            <universal-button
              type="base"
              :label="$t('road-maintenance/aggregator.move_data')"
              color="blue"
              style="align-self: end; font-size: 12px; color: #FFFFFF;"
              prepend-icon="mdi-arrow-left-bold"
              :prepend-icon-style="{ fontSize: '20px', color: '#FFFFFF !important' }"
              @click="moveCurrentDataToNewData"
            />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import textField from '@/global/components/view-layouts/tabs-layout/components/fields/TextField.vue'
import AutocompleteField from '@/global/components/view-layouts/tabs-layout/components/fields/AutocompleteField.vue'
import { isObject } from 'lodash'
import { api } from '@/global/services/api'
import store from '@/global/store'

export default {
  name: 'CompleteDataDialog',

  components: {
    AutocompleteField,
    textField,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    'close'
  ],

  data () {
    return {
      canShowDialog: false,
      oibOrName: '',
      oibOrNameField: {
        key: 'oibOrName',
        label: this.$t('road-maintenance/aggregator.enter_name'),
        editable: true
      },
      filteredResults: [],
      formData: {},
      showLoader: false,
      showUpdateBtn: false,
      validationErrors: {},
      currentResultId: null
    }
  },

  watch: {
    showDialog: {
      immediate: true,
      handler (val) {
        this.canShowDialog = !!val
      }
    },
    'config.currentData': {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && isObject(val) && this.formFields) {
          this.formFields.forEach(field => {
            if (field?.key && val[field.key]) {
              this.$set(this.formData, field.key + '-current', val[field.key])
            }
          })
        }
      }
    },
    'config.type': {
      immediate: true,
      handler (val) {
        if (['applicant', 'investor'].includes(val)) {
          this.$set(this.oibOrNameField, 'label', this.$t('road-maintenance/aggregator.enter_oib'))
        }
      }
    }
  },

  computed: {
    formFields () {
      const { type } = this.config || {}
      let fields = []

      if (!type) {
        return fields
      }

      if (type === 'applicant') {
        fields = [
          {
            key: 'applicant_first_name',
            label: this.$t('road-maintenance/aggregator.first_name'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_last_name',
            label: this.$t('road-maintenance/aggregator.last_name'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_oib',
            label: 'OIB*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_address_street',
            label: this.$t('road-maintenance/aggregator.address_street'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_address_number',
            label: this.$t('road-maintenance/aggregator.house_number'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_phone_number',
            label: this.$t('road-maintenance/aggregator.phone_number'),
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'applicant_email',
            label: this.$t('road-maintenance/aggregator.email_address'),
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }
      else if (type === 'investor') {
        fields = [
          {
            key: 'investor_name',
            label: this.$t('road-maintenance/aggregator.name') + '*',
            row: 1,
            column: 1,
            colSpan: 1.5,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'investor_address',
            label: this.$t('road-maintenance/aggregator.address') + '*',
            row: 1,
            column: 2,
            colSpan: 1.5,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'investor_oib',
            label: 'OIB*',
            row: 2,
            column: 1,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'investor_phone_number',
            label: this.$t('road-maintenance/aggregator.mobile_telephone') + '*',
            row: 2,
            column: 2,
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'investor_email',
            label: this.$t('road-maintenance/aggregator.email_address') + '*',
            row: 2,
            column: 3,
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }
      else if (type === 'contractor') {
        fields = [
          {
            key: 'contractor_name',
            label: this.$t('road-maintenance/aggregator.name') + '*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'contractor_responsible_person',
            label: this.$t('road-maintenance/aggregator.contractor_responsible_person') + '*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'contractor_phone_number',
            label: this.$t('road-maintenance/aggregator.mobile_telephone') + '*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'contractor_email',
            label: this.$t('road-maintenance/aggregator.email_address') + '*',
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }
      else if (type === 'installation_owner') {
        fields = [
          {
            key: 'installation_owner_name',
            label: this.$t('road-maintenance/aggregator.name') + '*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'installation_owner_phone_number',
            label: this.$t('road-maintenance/aggregator.mobile_telephone') + '*',
            creatable: true,
            editable: true,
            visible: true
          },
          {
            key: 'installation_owner_email',
            label: this.$t('road-maintenance/aggregator.email_address') + '*',
            creatable: true,
            editable: true,
            visible: true
          }
        ]
      }

      return fields
    },

    dialogTitle () {
      const { type } = this.config || {}
      let title = this.$t('road-maintenance/aggregator.confirm_data')

      if (!type) {
        return title
      }

      if (type === 'applicant') {
        title += ' - ' + this.$t('road-maintenance/prp.request_applicant')
      }
      else if (type === 'investor') {
        title += ' - ' + this.$t('road-maintenance/dropdown-types.investor')
      }
      else if (type === 'contractor') {
        title += ' - ' + this.$t('road-maintenance/dropdown-types.contractor')
      }
      else if (type === 'installation_owner') {
        title += ' - ' + this.$t('road-maintenance/aggregator.authorized_person')
      }

      return title
    }
  },

  async created () {
    try {
      const { type, currentData } = this.config || {}

      if (type && typeof type === 'string' && currentData && isObject(currentData)) {
        this.oibOrName = currentData[type].oib || currentData[type].name

        if (this.oibOrName) {
          await this.onSearchByOibOrName()
        }
      }
    }
    catch (exception) {
      console.error(exception)
    }
  },

  methods: {
    closeDialog (showButton = true, type = null, data = null, updateData = {}) {
      this.$emit('close', {
        showButton,
        type,
        data,
        updateData: (val) => updateData(val)
      })
    },

    async onSearchByOibOrName () {
      try {
        this.showLoader = true

        // Check if type is set
        if (!this.config.type || typeof this.config.type !== 'string') {
          this.showLoader = false
          store.dispatch('base/notifications/push', this.$t('road-maintenance/aggregator.missing_type'))
          return
        }

        // Validate input
        if (!this.oibOrName) {
          this.showLoader = false
          this.$set(this.validationErrors, 'oibOrName', ['applicant', 'investor'].includes(this.config.type) ? this.$t('road-maintenance/aggregator.enter_oib') : this.$t('road-maintenance/aggregator.enter_name'))
          return
        }

        const { data } = await api('road-maintenance').get('partner/search', {
          type: this.config.type.toUpperCase(),
          query: this.oibOrName
        })

        this.filteredResults = data
        this.showLoader = false
      }
      catch (exception) {
        console.error(exception)
        this.showLoader = false
      }
    },

    onSelectedResult (data) {
      try {
        this.showUpdateBtn = true
        const { id } = data || {}
        const { type } = this.config || {}

        this.$set(this.formData, 'id', id)
        this.currentResultId = id

        this.formFields.forEach(field => {
          const fieldKey = field.key ?? ''

          if (fieldKey) {
            const dataFieldKey = fieldKey.replace(type + '_', '').replace('_number', '')

            if (data[dataFieldKey]) {
              this.$set(this.formData, fieldKey, data[dataFieldKey])
            }
          }
        })
      }
      catch (exception) {
        console.error(exception)
      }
    },

    moveCurrentDataToNewData () {
      for (const key in this.formData) {
        if (key.endsWith('-current')) {
          const newKey = key.replace(/-current$/, '')
          this.$set(this.formData, newKey, this.formData[key])
        }
      }
      this.showUpdateBtn = false
      this.$delete(this.formData, 'id')
      this.currentResultId = null
    },

    async saveData (isUpdate = false) {
      const { type } = this.config || {}

      try {
        this.showLoader = true
        const { updateData } = this.config || {}

        if (!type || typeof type !== 'string') {
          this.showLoader = false
          store.dispatch('base/notifications/push', this.$t('road-maintenance/aggregator.missing_type'))
          return
        }

        // If user clicks create new btn, delete previously selected data id from form data
        if (!isUpdate) {
          this.$delete(this.formData, 'id')
        }

        const formData = new FormData()
        formData.append('_method', 'PATCH')
        formData.append('type', type.toUpperCase())

        for (const key in this.formData) {
          if (key && this.formData[key]) {
            // Do not include data from the right side
            if (!key.includes('-current')) {
              const newKey = key.replace('applicant_', '')
                .replace('investor_', '')
                .replace('contractor_', '')
                .replace('installation_owner_', '')
              formData.append(newKey, this.formData[key])
            }
          }
        }

        const response = await api('road-maintenance').post(`traffic-regulation/${this.$route.params.id}/update-partner-info`, formData)

        this.closeDialog(false, type, response, updateData)
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.showLoader = false

        if (errors && isObject(errors) && type && typeof type === 'string') {
          this.validationErrors = Object.entries(errors).reduce((acc, [key, value]) => {
            const cleanedKey = key
              .replace(/^applicant_/, '')
              .replace(/^investor_/, '')
              .replace(/^contractor_/, '')
              .replace(/^installation_owner_/, '')

            acc[`${type}_${cleanedKey}`] = value
            return acc
          }, {})
        }
      }
    },

    clearValidationError (key) {
      if (key && this.validationErrors[key]) {
        this.$delete(this.validationErrors, key)
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep.v-dialog__content .v-dialog {
    border-radius: 16px !important;
  }

.v-card-text {
  background-color: #FFFFFF;
  padding: 20px;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 24px !important;
  line-height: 32px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.x-image:hover {
  cursor: pointer;
}

.oib-wrapper-col {
  margin-bottom: 10%;
}

.border-right {
  border-right: 1px solid #E5E7EB;
}

.subtitle {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 28px !important;
  color: #111827 !important;
  margin-bottom: 10%;
}

.found-results-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #111827 !important;
}

.no-data-text {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 24px !important;
  color: #71717A;
}

.result-col {
  padding: 8px 8px 8px 5px;
  border: 1px solid #A1A1AA;
  border-radius: 8px;
}

.result-col:hover {
  background-color: #EDF9FF;
  border: 1px solid #163D73;
  cursor: pointer;
}

.result-col--active {
  background-color: #EDF9FF;
  border: 1px solid #163D73;
  cursor: pointer;
}

.result-row {
  margin: 10px 10px 10px 0;
}

.filtered-results-wrapper {
  max-height: 290px;
  overflow: auto;
}

.result-text-col {
  padding: 0 0 0 1rem;
  align-content: center;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 21px !important;
  color: #52525B !important;
  overflow-wrap: anywhere;
}
</style>
